import { UnisphereRuntimeLoader, UnisphereRuntimeVisual } from "@unisphere/runtime-react";
import { useContext, useMemo } from "react";
import { baseUrl, encodeEntryNameForURI, getEntryUrl, serverUrl } from "@kaltura/mediaspace-shared-utils";
import { ConfigContext, useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { version } from "@kaltura/mediaspace-shared-data-kms-version";
import { ChatRuntimeName, ChatRuntimeSettings, WidgetName } from "@unisphere/genie-core";
import { styled } from "@mui/material";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { headerHeight } from "@kaltura/mediaspace-shared-constants";
import { sidebarButtonsSize, useIsMobileSidebarLayout, useSidebarLayoutContext } from "@kaltura/ds-react-layouts";

export enum CategoryType {
    CHANNEL = "channel",
    GALLERY = "gallery",
}

const genieVisualType = "page";

export interface GenieProps {
    genieServerUrl: string;
    ks: string;
    uiconfId: string;
    categoryData?: { id: number; name: string; type: CategoryType };
    isFullSize?: boolean;
}

export const Genie = ({ genieServerUrl, ks, uiconfId, categoryData, isFullSize = false }: GenieProps) => {
    const {
        application: { partnerId, serviceUrl },
        analytics,
    } = useConfig();

    const configContext = useContext(ConfigContext);
    const headerColor = configContext.dsLayout?.headerColor;
    const topSpacing = headerColor && headerColor !== "transparent" ? headerHeight : 0;

    const { isEnabled } = useSidebarLayoutContext();
    const isMobileSidebarLayout = useIsMobileSidebarLayout();
    const bottomSpacing = isEnabled && isMobileSidebarLayout ? sidebarButtonsSize : 0;

    // Calculate minHeight style for full size mode container
    const minHeight = isFullSize ? `calc(100vh - ${topSpacing + bottomSpacing}px)` : undefined;

    const getCategoryUrl = () => {
        if (!categoryData) {
            return "";
        }

        switch (categoryData.type) {
            case CategoryType.CHANNEL:
                return `${baseUrl}/channel/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
            case CategoryType.GALLERY:
                return `${baseUrl}/category/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
            default:
                return "";
        }
    };

    const settings = useMemo<ChatRuntimeSettings>(
        () => ({
            getSourceUrl: ({ entryId, startTime }: { entryId: string; startTime?: number }) => {
                return getEntryUrl(entryId) + (startTime ? `?st=${startTime}` : "");
            },
            shareUrl: {
                queryParam: "mid",
                createUrl: ({ messageId }: { messageId: string }) => {
                    const host = serverUrl + (baseUrl ? `/${baseUrl}` : "");
                    if (categoryData) {
                        // if in context of category, preserve it in the share url
                        return `${host}/genie/index/index/categoryId/${categoryData.id}?mid=${messageId}`;
                    }
                    return `${host}/genie?mid=${messageId}`;
                },
            },
            genieServerUrl,
            kalturaServerURI: serviceUrl,
            ks,
            partnerId: partnerId.toString(),
            uiconfId,
            analytics: {
                enabled: true,
                serverUrl: "https://" + analytics.serviceUrl,
                appName: analytics.appName,
                appVersion: version,
            },
            ...(categoryData
                ? {
                      customization: {
                          breadcrumb: {
                              root: {
                                  url: getCategoryUrl(),
                                  label: categoryData.name,
                              },
                          },
                      },
                  }
                : {}),
        }),
        [analytics.appName, analytics.serviceUrl, categoryData, genieServerUrl, ks, partnerId, serviceUrl, uiconfId]
    );

    return (
        <StyledOuterWrapper isFullSize={isFullSize} minHeight={minHeight}>
            <UnisphereRuntimeLoader
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
                runtimeSettings={settings as unknown as Record<string, unknown>}
            />
            <UnisphereRuntimeVisual
                visualSettings={null}
                visualType={genieVisualType}
                widgetName={WidgetName}
                runtimeName={ChatRuntimeName}
            />
        </StyledOuterWrapper>
    );
};

const StyledOuterWrapper = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "isFullSize";
        },
    }
)(({ isFullSize, minHeight }: { isFullSize: boolean; minHeight?: string }) => [
    {
        position: "relative",
        width: "100%",
        minHeight,
    },
    isFullSize && {
        // full size mode - flex column to allow inner content to stretch
        display: "flex",
        flexDirection: "column",
        "& > div:first-of-type": {
            display: "flex",
            flex: "1",
        },
    },
]);
